import React from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shared/components/ui/context-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { User, Mail, Settings, Calendar, Search } from "lucide-react";

const OverlaysPage: React.FC = () => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Overlays
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Components that appear above the main content layer.
        </p>
      </header>

      <div className="space-y-16">
        {/* Command */}
        <section id="command" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Command</h2>
            <p className="mt-1 text-sm text-stone-500">
              Command palette for keyboard-driven navigation.
            </p>
          </div>
          <Command className="rounded-lg border shadow-md">
            <CommandInput placeholder="Type a command or search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Suggestions">
                <CommandItem>
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>Calendar</span>
                </CommandItem>
                <CommandItem>
                  <Search className="mr-2 h-4 w-4" />
                  <span>Search</span>
                </CommandItem>
                <CommandItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </section>

        {/* Context Menu */}
        <section id="context-menu" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Context Menu</h2>
            <p className="mt-1 text-sm text-stone-500">Right-click menu for contextual actions.</p>
          </div>
          <ContextMenu>
            <ContextMenuTrigger className="flex h-32 w-full items-center justify-center rounded-md border border-dashed border-stone-300 text-sm text-stone-500">
              Right-click here
            </ContextMenuTrigger>
            <ContextMenuContent className="w-56">
              <ContextMenuLabel>Actions</ContextMenuLabel>
              <ContextMenuSeparator />
              <ContextMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>View Profile</span>
              </ContextMenuItem>
              <ContextMenuItem>
                <Mail className="mr-2 h-4 w-4" />
                <span>Send Message</span>
              </ContextMenuItem>
              <ContextMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        </section>

        {/* Dialog */}
        <section id="dialog" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Dialog</h2>
            <p className="mt-1 text-sm text-stone-500">
              Modal windows for focused interactions and confirmations.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">Open Dialog</Button>
            </DialogTrigger>
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>Edit profile</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <DialogDescription>
                  Make changes to your profile here. Click save when you&apos;re done.
                </DialogDescription>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input id="name" defaultValue="Pedro Duarte" className="col-span-3" />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="username" className="text-right">
                      Username
                    </Label>
                    <Input id="username" defaultValue="@peduarte" className="col-span-3" />
                  </div>
                </div>
              </DialogBody>
              <DialogFooter onSubmit={() => setDialogOpen(false)} submitText="Save changes" />
            </DialogContent>
          </Dialog>
        </section>

        {/* Dropdown Menu */}
        <section id="dropdown-menu" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Dropdown Menu</h2>
            <p className="mt-1 text-sm text-stone-500">
              Contextual menus triggered by user interaction.
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Open Menu</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Mail className="mr-2 h-4 w-4" />
                <span>Mail</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </section>

        {/* Popover */}
        <section id="popover" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Popover</h2>
            <p className="mt-1 text-sm text-stone-500">
              Floating content panels anchored to trigger elements.
            </p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">Open Popover</Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Dimensions</h4>
                  <p className="text-sm text-muted-foreground">Set the dimensions for the layer.</p>
                </div>
                <div className="grid gap-2">
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="width">Width</Label>
                    <Input id="width" defaultValue="100%" className="col-span-2 h-8" />
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="maxWidth">Max. width</Label>
                    <Input id="maxWidth" defaultValue="300px" className="col-span-2 h-8" />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </section>

        {/* Sheet */}
        <section id="sheet" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Sheet</h2>
            <p className="mt-1 text-sm text-stone-500">
              Slide-out panel for secondary content and actions.
            </p>
          </div>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline">Open Sheet</Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Sheet Title</SheetTitle>
                <SheetDescription>
                  This is a sheet component that slides in from the side.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                <p>Sheet content goes here.</p>
              </div>
            </SheetContent>
          </Sheet>
        </section>

        {/* Tooltip */}
        <section id="tooltip" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Tooltip</h2>
            <p className="mt-1 text-sm text-stone-500">
              Contextual information on hover. Pass a title to show a header above the body, like
              "Why we ask" on sensitive fields. Use TooltipInfoTrigger for the Info icon next to a
              field label.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-secondary">Default</p>
              <Label className="flex items-center gap-1 text-sm font-medium">
                Ownership type <span className="text-secondary">(optional)</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipInfoTrigger />
                    <TooltipContent>
                      How the account was owned determines who inherits it and how it transfers.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input placeholder="Joint with right of survivorship" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-secondary">With title</p>
              <Label className="flex items-center gap-1 text-sm font-medium">
                Phone number
                <TooltipProvider>
                  <Tooltip>
                    <TooltipInfoTrigger aria-label="Why we ask" />
                    <TooltipContent title="Why we ask">
                      We'll call or text you when the estate needs you, like a form to sign or a
                      question from a bank. It's the fastest way to keep things moving between
                      visits.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input placeholder="(555) 555-0142" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OverlaysPage;
